import React, { useEffect, useMemo, useState } from "react";
import "./PermissionsChecklist.scss";
import InfoIcon from "../../components/infoIcon/InfoIcon";
import Alert, { AlertType } from "../../components/Alert";

/**
 * Permission shape:
 * { Name: string; DisplayName?: string; Description?: string }
 *
 * - readOnly: when true, renders ONLY the assigned permissions (no checkboxes).
 * - allAvailablePermissions: full catalogue of permissions.
 * - assignedPermissions: currently assigned permissions (same shape as catalogue).
 * - onChange(nextAssignedPermissions): fired on toggle (editable mode).
 * - disableTogglingManageAdminUsersPermission: disables the check box for Manage Admin Users
 */
export default function PermissionsChecklist({
	readOnly = false,
	allAvailablePermissions = [],
	assignedPermissions = [],
	onChange,
	disableTogglingManageAdminUsersPermission
}) {

	const sortedAll = useMemo(() => {
		const key = (p) => p?.DisplayName ?? p?.Name ?? "";
		return [...allAvailablePermissions].sort((a, b) =>
			key(a).localeCompare(key(b), undefined, { sensitivity: "base" })
		);
	}, [allAvailablePermissions]);

	// Build set of selected names from assignedPermissions prop
	const selectedNames = useMemo(() => {
		return new Set(
			(assignedPermissions ?? [])
				.map((p) => p?.Name)
				.filter((n) => typeof n === "string" && n.length > 0)
		);
	}, [assignedPermissions]);

	// Local selected set (for instant UI response). Keep in sync with prop.
	const [selected, setSelected] = useState(selectedNames);

	useEffect(() => {
		const same =
			selected.size === selectedNames.size &&
			[...selected].every((n) => selectedNames.has(n));
		if (!same) {
			setSelected(new Set(selectedNames));
		}
	}, [selectedNames, selected]);

	// Toggle (editable mode only)
	const handleToggle = (name) => (e) => {
		if (readOnly) {
			return;
		}
		const next = new Set(selected);
		if (e.target.checked === true) {
			next.add(name);
		}
		else {
			next.delete(name);
		}
		setSelected(next);

		if (typeof onChange === "function") {
			const nextAssigned = allAvailablePermissions.filter((p) =>
				next.has(p.Name)
			);
			onChange(nextAssigned);
		}
	};

	// Which permissions to render
	const toRender = useMemo(() => {
		return readOnly
			? sortedAll.filter((p) => selected.has(p.Name))
			: sortedAll;
	}, [readOnly, sortedAll, selected]);

	return (
		<div aria-labelledby="permissions-heading">
			<h3 id="permissions-heading" className="section-title">
				Permissions
			</h3>

			{disableTogglingManageAdminUsersPermission &&
				<Alert type={AlertType.Warning}>
					You are not able to remove your own permission to Manage Admin Users because there needs to be at least one user with this permission.
				</Alert>
			}

			<div className="permissions-list" aria-label="Permissions">
				{toRender.map((p) => {
					const id = `perm-${p.Name}`;
					const checked = selected.has(p.Name);

					return readOnly ? (
						<div className="permissions-list-row" key={p.Name}>
							<p className="permission-label">{p.DisplayName ?? p.Name}</p>
							<span className="permissions-list-row-icon" aria-hidden="true">
								<InfoIcon text={p.Description} />
							</span>
						</div>
					) : (
						<div className="permissions-list-row" key={p.Name}>
							<input
								id={id}
								type="checkbox"
								className="permission-checkbox"
								checked={checked}
								onChange={handleToggle(p.Name)}
								aria-label={`toggle ${p.DisplayName ?? p.Name}`}
								disabled={p.Name === "ManageAdminUsers" && disableTogglingManageAdminUsersPermission}
							/>
							<label htmlFor={id} className="permission-label">
								{p.DisplayName ?? p.Name}
							</label>
							<span className="permissions-list-row-icon" aria-hidden="true">
								<InfoIcon text={p.Description} />
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}